/* eslint-disable object-shorthand */
const resolveByCountry = require('@dc/ui-library/src/helpers/environment').resolveByCountry;
const dataLayerUtils = require('@dc/data-layer/src/utils.js');
const dayjs = require('dayjs');

const Analytics = {
  /** @private */
  registeredEvents: {},

  Events: {},

  /** @param {string} name
      @param {function(data) : void} handler
      @returns {void} */
  registerTrackingEvent: function registerTrackingEvent(name, handler) {
    // console.log("Registering Tracking Event", name);
    this.Events[name] = name;
    this.registeredEvents[name] = handler;
  },

  /** @description Call Analytics.track("TrackingEvent", optionalData) to
      dispatch the tracking handler
      @param {string} name
      @param {string|number|object} [data]
      @returns {void} */
  track: function track(name, data) {
    if (this.registeredEvents[name] === undefined) {
      throw new Error(`Analytics Event Not Defined: ${name}`);
    }

    const args = [];
    let i;

    for (i = 1; i < arguments.length; i++) { // Convert & strip first event name arg
      args.push(arguments[i]); // eslint-disable-line prefer-rest-params
    }

    this.registeredEvents[name].apply(null, args);
  },

  /** @param {string} event
      @param {object} [data]
      @param {string} [immediate] Immediate tracking event to dispatch
      @returns {void} */
  dispatch: function dispatch(event, data, immediate) {
    // console.log("Dispatching Analytics Event :'", event +"'", data);

    if (immediate !== undefined) {
      dataLayerUtils.satelliteTrack(immediate);
    }
  }
};

Analytics.dispatchDeliveryOptionSelected = function dispatchDeliveryOptionSelected(slotData) {

  // digitalData layer state dispatch
  if (window.appState) {
    window.appState.dispatch(
      {
        type: 'EVENTS_PRODUCT_PAGE_DELIVERY_OPTION_SELECTED',
        payload: {
          deliveryDay: slotData.date,
          deliveryTime: slotData.label,
          // we use for today time from web browser
          // we suppose only UK users use this web
          // it can generate wrong data in a timezone outside UK
          // this has to be totally redone for using outside UK (especially outside Europe)
          daysAway: Number(
            dayjs(slotData.date.replace(/\//g, '-')).diff(dayjs().startOf('day'), 'days') // replace converts 'YYYY/MM/DD' to 'YYYY-MM-DD'
          ),
          deliveryPrice: parseAndReturnDeliveryPrice(slotData.price)
        }
      }
    );

    dataLayerUtils.satelliteTrack('addToBasket'); // eslint-disable-line no-underscore-dangle
  }
};

Analytics.dispatchFreeDeliveryOptionSelected = function dispatchFreeDeliveryOptionSelected() {

  if (window.appState) {
    window.appState.dispatch(
      {
        type: 'EVENTS_PRODUCT_PAGE_FREE_DELIVERY_OPTION_SELECTED',
        payload: {
          deliveryDay: null,
          deliveryTime: null,
          daysAway: null,
          deliveryPrice: 0
        }
      }
    );

    dataLayerUtils.satelliteTrack('addToBasket'); // eslint-disable-line no-underscore-dangle
  }
};

Analytics.addDeliverySlotsToAnalytics =
  function addDeliverySlotsToAnalytics(deliverySlots, productType) {
    sortDeliverySlotsByDate(deliverySlots);

    triggerAnalyticsActionsInDataLayer(
      generateAndReturnOutputDataForAnalyticsFromSortedSlots(deliverySlots, productType),
      productType
    );
  };

function sortDeliverySlotsByDate(deliverySlots) {
  // sort from Array.prototype is mutable
  deliverySlots.sort((delSlotsA, delSlotsB) => {
    const detSlotsATimestamp = dayjs(delSlotsA.deliverySlots[0].date.replace(/\//g, '-')).valueOf(); // replace converts 'YYYY/MM/DD' to 'YYYY-MM-DD'
    const detSlotsBTimestamp = dayjs(delSlotsB.deliverySlots[0].date.replace(/\//g, '-')).valueOf(); // replace converts 'YYYY/MM/DD' to 'YYYY-MM-DD'

    return detSlotsATimestamp - detSlotsBTimestamp;
  });
}

// see MCD-3286 (smallboxes) and MCD-3323 (bixboxes) on Jira for understanding
// the format of output which is generated by this function
function generateAndReturnOutputDataForAnalyticsFromSortedSlots(sortedDeliverySlots, productType) {
  const deliverySlotsOutputArray = [];

  sortedDeliverySlots.forEach((currentDelSlot) => {
    let currentDeliverySlotsOutputArrayLength;

    const diffBetweenTodayAndCurrentSlotDayInDays =
      getDiffBetweenTodayAndCurrentSlotDayInDays(currentDelSlot.deliverySlots);

    // add null to array for days items until current day where is no delivery slot
    if (deliverySlotsOutputArray.length !== diffBetweenTodayAndCurrentSlotDayInDays &&
      diffBetweenTodayAndCurrentSlotDayInDays - deliverySlotsOutputArray.length > 0) {

      currentDeliverySlotsOutputArrayLength = deliverySlotsOutputArray.length;
      for (let i = currentDeliverySlotsOutputArrayLength;
        i !== diffBetweenTodayAndCurrentSlotDayInDays; i++) {
        deliverySlotsOutputArray[i] = null;
      }
    }

    deliverySlotsOutputArray.push([]);
    currentDelSlot.deliverySlots.forEach((currentSlot) => {
      let currentOutputSlotForAnalytics;

      // If a slot is freeDelivery then a slot button is not generated for it
      // Separate free delivery button is generated for it in MCD panel above or
      // below the slots button. That's why it's not added to analytics output
      if (!currentSlot.freeDelivery) {
        currentOutputSlotForAnalytics = {
          deliveryDay: currentSlot.date,
          deliveryTime: currentSlot.label,
          daysAway: deliverySlotsOutputArray.length - 1,
          deliveryPrice: parseAndReturnDeliveryPrice(currentSlot.price)
        };
        deliverySlotsOutputArray[deliverySlotsOutputArray.length - 1]
          .push(currentOutputSlotForAnalytics);
      }
    });
  });

  return deliverySlotsOutputArray;
}

function triggerAnalyticsActionsInDataLayer(deliverySlotsOutputArray, productType) {
  if (window.appState && typeof window.appState.dispatch === 'function') {
    if (productType === 'SMALLBOX') {
      window.appState.dispatch(
        {
          type: 'SMALL_BOX_PRODUCT_DELIVERY_SLOTS_SET',
          payload: {
            sbSlots: deliverySlotsOutputArray
          }
        }
      );
    }

    if (productType === 'BIGBOX') {
      window.appState.dispatch(
        {
          type: 'BIG_BOX_PRODUCT_DELIVERY_SLOTS_SET',
          payload: {
            bbSlots: deliverySlotsOutputArray
          }
        }
      );
    }
  }
}

function getDiffBetweenTodayAndCurrentSlotDayInDays(deliverySlots) {
  let currentSlotDate;

  // we use for today time from web browser
  // we suppose only UK users use this web
  // it can generate wrong data in a timezone outside UK
  // this has to be totally redone for using outside UK (especially outside Europe)
  const today = dayjs().startOf('day');

  deliverySlots.some((currentSlot) => {
    if (currentSlot.freeDelivery) {
      return false;
    }
    // currentSlotDate is always here defined because deliverySlots
    // can never contain only freeDelivery slots
    currentSlotDate = currentSlot.date;
    return true;
  });

  return dayjs(
    currentSlotDate
      ? currentSlotDate.replace(/\//g, '-') // replace converts 'YYYY/MM/DD' to 'YYYY-MM-DD'
      : null
  ).diff(today, 'days');
}

function parseAndReturnDeliveryPrice(priceString) {
  /* removing '£'pound and € euro sign from the string using html character entity
   * because it was parsed from html.
   * It also replaces pound and euro as unicode sign to make this solution proved for future
   * if pound will be gathered from JSON from an AJAX call response
   */

  const priceNumber =
    Number(priceString.replace(resolveByCountry('&pound;', '&euro;'), '').replace()
      .replace(resolveByCountry(/\u00A3/g, /\u20AC/g), ''));

  if (Number.isNaN(priceNumber)) {
    return 0;
  }

  return priceNumber;
}

function removeNonBreakingSpaceFromString(stringWithNonBreakingSpace) {
  return stringWithNonBreakingSpace.replace('&nbsp;', ' ');
}

function createProperPriceNumberFormatForAnalytics(priceString) {
  return Number(priceString.replace(resolveByCountry('&pound;', '&euro;'), '')
    .replace(resolveByCountry('£', '€'), '').replace(/,/g, ''));
}

// Dispatch when user clicks on compare button or on label/link next to checkbox
Analytics.dispatchClickCompareButton = function dispatchClickCompareButton(productsIDs) {
  if (window.appState) {
    window.appState.dispatch({
      type: 'COMPARE_BUTTON_ONCLICK',
      payload: {
        products: productsIDs,
        trackFunctionArgs: 'compareItems'
      }
    });
  }
};

// Uncheck product from comparism analytics dispatcher
Analytics.dispatchUncheckProduct = function dispatchUncheckProduct(product) {
  if (window.appState) {
    window.appState.dispatch({
      type: 'UNCHECK_PRODUCT_FROM_COMPARE',
      payload: {
        products: product.productID,
        trackFunctionArgs: 'removeComparisonItem'
      }
    });
  }
};

// When popup is displayed dispatch id of the product for analytics
Analytics.dispatchClickProductImage = function dispatchClickProductImage(productID) {
  if (window.appState) {
    window.appState.dispatch({
      type: 'COMPARE_PANEL_DISPLAY_POPUP',
      payload: {
        products: productID,
        trackFunctionArgs: 'comparisonListClick'
      }
    });
  }
};

// When a user clicks on the comparison check box dispatch populate the events
Analytics.dispatchComparisonCheckClick =
function dispatchComparisonCheckClick(productID, checkboxStatus, errorMessage) {
  if (window.appState) {
    window.appState.dispatch({
      type: 'CLICK_ON_CHECKBOX',
      payload: {
        products: productID,
        status: checkboxStatus,
        error: errorMessage,
        trackFunctionArgs: 'comparisonCheckClick'
      }
    });
  }
};

Analytics.setAvailableSwatchesForProductOnProductPage =
  function setAvailableSwatchesForProductOnProductPage(availableSwatches) {
    const availableSwatchesForDataLayer = [];

    if (window.appState.dispatch) {
      availableSwatches.forEach((currentSwatchesObject) => {
        currentSwatchesObject.variants.forEach((currentVariant) => {
          availableSwatchesForDataLayer.push({
            swatchAttributeType: currentSwatchesObject.title,
            swatchName: removeNonBreakingSpaceFromString(currentVariant.variant),
            swatchID: currentVariant.fupid,
            swatchPrice: typeof currentVariant.price === 'number'
              ? currentVariant.price
              : createProperPriceNumberFormatForAnalytics(currentVariant.price)
          });
        });
      });

      window.appState.dispatch(
        {
          type: 'PRODUCT_AVAILABLE_SWATCHES_SET',
          payload: {
            availableSwatches: availableSwatchesForDataLayer
          }
        }
      );
    }
  };

Analytics.onSwatchClickOnProductPage = function onSwatchClickOnProductPage(chosenSwatch,
  chosenSwatchID, chosenSwatchAttributeType, numberOfSwatches) {
  if (window.appState) {
    window.appState.dispatch(
      {
        type: 'EVENTS_PRODUCT_PAGE_SWATCH_CLICKED',
        payload: {
          chosenSwatch: removeNonBreakingSpaceFromString(chosenSwatch),
          chosenSwatchID: chosenSwatchID,
          chosenSwatchAttributeType: chosenSwatchAttributeType,
          numberOfSwatches: numberOfSwatches
        }
      }
    );
  }
};

Analytics.onAddingSecondaryEmailForRnC =
  function onAddingSecondaryEmailForRnC(addedSecondaryEmail, isValidEmail) {
    if (window.appState) {
      window.appState.dispatch(
        {
          type: 'ADDED_SECOND_EMAIL',
          payload: {
            addedSecondaryEmail: addedSecondaryEmail,
            isValidEmail: isValidEmail,
            trackFunctionArgs: 'Add second email'
          }
        }
      );
    }
  };

Analytics.onSubmittingRnCForm =
  function onSubmittingRnCForm(requiredFieldsFilled, formSubmissionSuccessful, fieldsNotFilled) {
    if (window.appState) {
      window.appState.dispatch(
        {
          type: 'RNC_FORM_SUBMITTED',
          payload: {
            requiredFieldsFilled: requiredFieldsFilled,
            formSubmissionSuccessful: formSubmissionSuccessful,
            fieldsNotFilled: fieldsNotFilled,
            trackFunctionArgs: 'RnC Form Submit'
          }
        }
      );
    }
  };

Analytics.onProductAttachmentToBasket = function onProductAttachmentToBasket(productID) {
  if (window.appState) {
    window.appState.dispatch({
      type: 'PRODUCT_ATTACHMENT_TO_BASKET',
      payload: {
        productID: productID
      }
    });
  }

  dataLayerUtils.satelliteTrack('attachPopupAddToBasket');
};

Analytics.trackProductAttachmentsPopupDisplayed =
  function trackProductAttachmentsPopupDisplayed(
    productID,
    bundleType,
    recommendationType,
    productsDisplayed
  ) {
    if (window.appState) {
      window.appState.dispatch({
        type: 'PRODUCT_ATTACHMENT_POPUP_OPEN',
        payload: {
          data: {
            recommendationType: recommendationType,
            bundleType: bundleType,
            productID: productID
          },
          productsDisplayed: productsDisplayed
        }
      });
    }

    dataLayerUtils.satelliteTrack('attachPopupDisplayed');
  };

Analytics.trackGeneralEvent = function trackGeneralEvent(
  eventName,
  eventAction,
  data,
  trackLabel = 'generalEvent'
) {
  if (window.appState) {
    window.appState.dispatch({
      type: 'GENERAL_EVENT',
      payload: {
        eventName: eventName,
        eventAction: eventAction,
        data: data
      }
    });
  }

  dataLayerUtils.satelliteTrack(trackLabel);
};

Analytics.onProductAttachmentSelection = function onProductAttachmentSelect(productID, selected) {
  if (window.appState) {
    window.appState.dispatch({
      type: 'PRODUCT_ATTACHMENT_SELECTION',
      payload: {
        productID: productID,
        boxStatus: selected ? 'productAdded' : 'productRemoved'
      }
    });
  }

  dataLayerUtils.satelliteTrack('attachPopupProductSelection');
};

// PREX-1757 PSA Recycling & Installation --------------------------------------

Analytics.trackPSAServicesDisplayed =
  function trackPSAServicesDisplayed(
    services /* same format as from fetchServices() */
  ) {
    trackPSAEvent({
      type: 'PSA_SERVICES_DISPLAYED',
      services
    });

    dataLayerUtils.satelliteTrack('PSAServicesDisplayed');
  };

Analytics.trackPSAServiceToBasket =
  function trackPSAServiceToBasket(service /* single service, same format as from fetchServices() */) { // eslint-disable-line max-len
    trackPSAEvent({
      type: 'PSA_SERVICE_TO_BASKET',
      services: [service]
    });

    dataLayerUtils.satelliteTrack('PSAServiceToBasket');
  };

Analytics.trackPSAServiceOffBasket =
  function trackPSAServiceOffBasket(service /* single service, same format as from fetchServices() */) { // eslint-disable-line max-len
    trackPSAEvent({
      type: 'PSA_SERVICE_OFF_BASKET',
      services: [service]
    });

    dataLayerUtils.satelliteTrack('PSAServiceOffBasket');
  };

function trackPSAEvent(
  { type, services = [] /* same format as from fetchServices() */ }
) {
  if (window.appState && type) {
    window.appState.dispatch({
      type,
      payload: {
        servicesDisplayed: formatServicesForTracking(services)
      }
    });
  }

  dataLayerUtils.satelliteTrack('attachPopupDisplayed');
}

/**
 * converts original format as from fetchServices() to format defined in PREX-1757 req#1,101 - 1,107
 *
 * @example
 *  services = [{
 *    productID: '530', // string, FUPID of the service
 *    serviceName: 'Recycling',
 *    currentPrice: 16.99, // if applicable, price of the service, number, default = 0
 *    savingPrice: 3, // if applicable, ... , default = 0
 *    wasPrice: 19.99 // if applicable, ... , default = 0
 *  }]
 */
function formatServicesForTracking(services) {
  return services.map(({ id, type, price = {} }) => ({
    productID: `${id}`,
    serviceName: type,
    currentPrice: price.amountWithVat || 0,
    savingPrice: 0, // no data for this field yet, TODO?
    wasPrice: 0 // no data for this field yet, TODO?
  }));
}

// -----------------------------------------------------------------------------

Analytics.priceMatchOpen = function priceMatchOpen(productId) {
  if (window.appState) {
    window.appState.dispatch({
      type: 'PRICE_MATCH_OPEN',
      payload: {
        productId
      }
    });
  }

  dataLayerUtils.satelliteTrack('PriceMatchOpen');
};

Analytics.priceMatchClose = function priceMatchClose(productId) {
  if (window.appState) {
    window.appState.dispatch({
      type: 'PRICE_MATCH_CLOSE',
      payload: {
        productId
      }
    });
  }

  dataLayerUtils.satelliteTrack('PriceMatchClose');
};

Analytics.requestPriceMatch = function requestPriceMatch(productId) {
  if (window.appState) {
    window.appState.dispatch({
      type: 'REQUEST_PRICE_MATCH',
      payload: {
        productId
      }
    });
  }

  dataLayerUtils.satelliteTrack('RequestPriceMatch');
};

Analytics.creditMassageClose = function creditMassageClose(productId) {
  if (window.appState) {
    window.appState.dispatch({
      type: 'CREDIT_MESSAGE_CLOSE',
      payload: {
        productId
      }
    });
  }

  dataLayerUtils.satelliteTrack('CreditMassageClose');
};

Analytics.creditMassageOpen = function creditMassageOpen(productId) {
  if (window.appState) {
    window.appState.dispatch({
      type: 'CREDIT_MESSAGE_OPEN',
      payload: {
        productId
      }
    });
  }

  dataLayerUtils.satelliteTrack('CreditMassageOpen');
};

Analytics.creditMassageMoreInfo = function creditMassageMoreInfo(productId) {
  if (window.appState) {
    window.appState.dispatch({
      type: 'CREDIT_MESSAGE_MORE_INFO',
      payload: {
        productId
      }
    });
  }

  dataLayerUtils.satelliteTrack('CreditMassageMoreInfo');
};

// info about if boxed or unboxed switch clicked
Analytics.productSizingShowMeLink = function productSizingShowMeLink(showMeLink) {
  if (window.appState) {
    window.appState.dispatch({
      type: 'PRODUCT_SIZING_SHOW_ME_LINK',
      payload: {
        showMeLink
      }
    });
  }
};

// info about mm/inches switch clicked
Analytics.productSizingUnits = function productSizingUnits(units) {
  if (window.appState) {
    window.appState.dispatch({
      type: 'PRODUCT_SIZING_UNITS',
      payload: {
        units
      }
    });
  }
};

// info about video played or completed to the end
Analytics.productSizingVideo = function productSizingVideo(video) {
  if (window.appState) {
    window.appState.dispatch({
      type: 'PRODUCT_SIZING_VIDEO',
      payload: {
        video
      }
    });
  }
};

// search term selected from suggestions
Analytics.selectedSuggestion = function selectedSuggestion(suggestionType) {
  if (window.appState) {
    window.appState.dispatch({
      type: 'SELECT_SUGGESTED_ITEM',
      payload: {
        suggestionType
      }
    });
  }
  dataLayerUtils.satelliteTrack('SuggestionSelected');
};

// notifications for save for later products price drop
Analytics.priceDropNotifications = function priceDropNotifications(cValue, pValue, fupid) {
  if (window.appState) {
    window.appState.dispatch({
      type: 'PRICE_DROP_NOTIFICATIONS',
      payload: {
        currentValue: cValue,
        previousValue: pValue,
        fupid: fupid
      }
    });
  }
  dataLayerUtils.satelliteTrack('Direct Call');
};

export default Analytics;

export const {
  Events,
  registeredEvents,
  registerTrackingEvent,
  track,
  dispatch,
  dispatchDeliveryOptionSelected,
  dispatchFreeDeliveryOptionSelected,
  addDeliverySlotsToAnalytics,
  dispatchClickCompareButton,
  dispatchUncheckProduct,
  dispatchClickProductImage,
  dispatchComparisonCheckClick,
  setAvailableSwatchesForProductOnProductPage,
  onSwatchClickOnProductPage,
  onAddingSecondaryEmailForRnC,
  onSubmittingRnCForm,
  onProductAttachmentToBasket,
  trackProductAttachmentsPopupDisplayed,
  trackGeneralEvent,
  trackPSAServicesDisplayed,
  trackPSAServiceToBasket,
  trackPSAServiceOffBasket,
  onProductAttachmentSelection,
  priceMatchOpen,
  priceMatchClose,
  requestPriceMatch,
  creditMassageClose,
  creditMassageOpen,
  creditMassageMoreInfo,
  productSizingShowMeLink,
  productSizingUnits,
  productSizingVideo,
  selectedSuggestion,
  priceDropNotifications
} = Analytics;

require('./analytics.events');
